import React, { useState, useMemo, useEffect } from 'react';
import './MeetingParticipantsModal.scss';
import Rodal from '../../../../components/Rodal/Rodal';
import { ModalHeader, ModalBody, Button, Input } from 'reactstrap';
import BootstrapTable from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { useCurrentMarketplaceParty } from '../../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { useStateSelector } from '../../../../store/selectors';
import { USER_ROLES } from '../../../../utils/constants';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import createNotification from '../../../../utils/createNotification';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { EngagementMeetingParticipant } from '../../../../slices/marketplace/meetings/models/engagement-meeting-participant';
import { MeetingParticipantSource } from '../../../../slices/marketplace/meetings/models/enums/meeting-participant-source.enum';
import { MarketplaceBuyerParticipantContract } from '../../../../slices/marketplace/meetings/models/marketplace-buyer-participant-contract';
import AddParticipantFormPopup, {
    AddParticipantFormValues,
} from './AddParticipantFormPopup';
import { canDeleteParticipant, deriveIsSelfAbsent } from './MeetingParticipantsModal.helpers';
import { canBuyerTeamMemberManageMeetings } from '../../../../utils/meetingSchedulingHelper';
import Helpers from '../../../../utils/helper';
import {
    MeetingFormat,
    MeetingFormatTypesOptions,
} from '../../../../slices/marketplace/meetings/models/enums/meeting-format.enum';
import { EngagementMeetingStatus } from '../../../../slices/marketplace/meetings/models/enums/meeting-status.enum';
import { EligibleMeetingUser } from '../../../../slices/marketplace/meetings/models/eligible-meeting-user';
import {
    MeetingAttendanceMode,
    MeetingAttendanceModeLabels,
} from '../../../../slices/marketplace/meetings/models/enums/meeting-attendance-mode.enum';

interface MeetingParticipantsModalProps {
    isVisible: boolean;
    onClose: () => void;
    engagementId: number;
    meetingId: string;
    isReadOnly?: boolean;
    meetingFormat?: MeetingFormat;
    meetingStartDate?: Date | string;
    meetingStatus?: EngagementMeetingStatus;
}

const MeetingParticipantsModal = (props: MeetingParticipantsModalProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const summary = useStateSelector((state) => state.userSummary.summary);
    const currentUser = useStateSelector((state) => state.currentUser.user);
    const party = useCurrentMarketplaceParty(props.engagementId);

    const canManageGlobalParticipants =
        party !== TimelineParty.Buyer ||
        canBuyerTeamMemberManageMeetings(summary, props.engagementId);

    const isAfterMeetingStart =
        props.meetingStartDate != null &&
        new Date() > new Date(props.meetingStartDate);

    const isMeetingCompleted =
        props.meetingStatus === EngagementMeetingStatus.Completed;

    const isParticipantsReadOnly = isAfterMeetingStart || isMeetingCompleted;

    const [isAddParticipantPopupVisible, setIsAddParticipantPopupVisible] =
        useState(false);
    const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] =
        useState(false);
    const [participantToDelete, setParticipantToDelete] =
        useState<EngagementMeetingParticipant | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [localParticipants, setLocalParticipants] = useState<
        EngagementMeetingParticipant[]
    >([]);
    const [buyerParticipants, setBuyerParticipants] = useState<
        MarketplaceBuyerParticipantContract[]
    >([]);
    const [eligibleRealUsers, setEligibleRealUsers] = useState<EligibleMeetingUser[]>([]);

    const fetchParticipants = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(
                `/api/Marketplace/Engagements/${props.engagementId}/MeetingScheduling/${props.meetingId}/Participants`
            );
            if (response.status === 200) {
                setLocalParticipants(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch participants:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchBuyerParticipants = async () => {
        try {
            const response = await axios.get(
                '/api/Marketplace/MarketplaceBuyerParticipants'
            );
            if (response.status === 200) {
                setBuyerParticipants(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch buyer participants:', error);
        }
    };

    const isManagerBuyer = canManageGlobalParticipants && party === TimelineParty.Buyer;
    const isManagerSeller = party === TimelineParty.Seller;

    const fetchEligibleRealUsers = async () => {
        if (!isManagerBuyer && !isManagerSeller) return;
        try {
            const response = await axios.get(
                `/api/Marketplace/Engagements/${props.engagementId}/MeetingScheduling/${props.meetingId}/Participants/EligibleRealUsers`
            );
            if (response.status === 200) {
                setEligibleRealUsers(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch eligible real users:', error);
        }
    };

    useEffect(() => {
        if (props.isVisible && props.engagementId && props.meetingId) {
            fetchParticipants();
            if (party === TimelineParty.Buyer) {
                fetchBuyerParticipants();
                fetchEligibleRealUsers();
            } else if (party === TimelineParty.Seller) {
                fetchEligibleRealUsers();
            }
        }
    }, [props.isVisible, props.engagementId, props.meetingId]);

    const sortedParticipants = useMemo(() => {
        if (!localParticipants || localParticipants.length === 0) {
            return [];
        }

        const getRank = (p: EngagementMeetingParticipant): number => {
            if (
                p.party === TimelineParty.Seller &&
                p.source === MeetingParticipantSource.TeamMember
            )
                return 0;
            if (
                p.party === TimelineParty.Buyer &&
                p.source === MeetingParticipantSource.TeamMember
            )
                return 1;
            return 2;
        };

        return [...localParticipants].sort((a, b) => getRank(a) - getRank(b));
    }, [localParticipants]);

    const selfParticipant =
        localParticipants.find(
            (p) =>
                p.source === MeetingParticipantSource.TeamMember &&
                p.userId === currentUser?.id
        ) ?? null;

    const isSelfAbsent = deriveIsSelfAbsent(localParticipants, currentUser?.id);

    const canLeaveMeeting = selfParticipant != null &&
        localParticipants.filter(p => p.party === selfParticipant.party).length > 1;

    const canDelete = (participant: EngagementMeetingParticipant): boolean =>
        canDeleteParticipant(participant, localParticipants, party, canManageGlobalParticipants, currentUser?.id);

    const handleJoinMeeting = async () => {
        try {
            setIsSubmitting(true);
            const response = await axios.post(
                `/api/Marketplace/Engagements/${props.engagementId}/MeetingScheduling/${props.meetingId}/Participants/Self`
            );
            if (response.status === 200 || response.status === 201) {
                createNotification('You have joined the meeting', 'success');
                await fetchParticipants();
            } else {
                createNotification('Failed to join meeting', 'error');
            }
        } catch (error: any) {
            createNotification('Failed to join meeting', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddRealUser = async (targetUserId: number) => {
        try {
            setIsSubmitting(true);
            const response = await axios.post(
                `/api/Marketplace/Engagements/${props.engagementId}/MeetingScheduling/${props.meetingId}/Participants/RealUser`,
                { userId: targetUserId }
            );
            if (response.status === 200 || response.status === 201) {
                createNotification('Participant added successfully', 'success');
                await Promise.all([fetchParticipants(), fetchEligibleRealUsers()]);
            } else {
                createNotification('Failed to add participant', 'error');
            }
        } catch (error: any) {
            createNotification('Failed to add participant', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddParticipant = async (values: AddParticipantFormValues) => {
        try {
            setIsSubmitting(true);

            let response;
            if (values.shareToList) {
                response = await axios.post(
                    '/api/Marketplace/MarketplaceBuyerParticipants',
                    {
                        name: values.name,
                        role: values.role,
                        engagementId: props.engagementId,
                        meetingProposalId: props.meetingId,
                    }
                );
            } else {
                response = await axios.post(
                    `/api/Marketplace/Engagements/${props.engagementId}/MeetingScheduling/${props.meetingId}/Participants`,
                    {
                        name: values.name,
                        role: values.role,
                    }
                );
            }

            if (response.status === 200 || response.status === 201) {
                createNotification('Participant added successfully', 'success');
                setIsAddParticipantPopupVisible(false);
                await Promise.all([
                    fetchParticipants(),
                    fetchBuyerParticipants(),
                ]);
            } else {
                createNotification('Failed to add participant', 'error');
            }
        } catch (error: any) {
            createNotification('Failed to add participant', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const initialSelectedBuyerIds = useMemo(() => {
        if (party !== TimelineParty.Buyer || buyerParticipants.length === 0)
            return [];
        const linkedIds = new Set(
            localParticipants
                .map((p) => p.buyerParticipantId)
                .filter((id): id is number => id != null)
        );
        return buyerParticipants
            .filter((bp) => linkedIds.has(bp.id))
            .map((bp) => bp.id);
    }, [buyerParticipants, localParticipants, party]);

    const handleDeleteBuyerParticipant = async (id: number) => {
        try {
            const response = await axios.delete(
                `/api/Marketplace/MarketplaceBuyerParticipants/${id}`
            );
            if (response.status === 200 || response.status === 204) {
                createNotification(
                    'Participant deleted from all meetings',
                    'success'
                );
                await Promise.all([
                    fetchParticipants(),
                    fetchBuyerParticipants(),
                ]);
            } else {
                createNotification('Failed to delete participant', 'error');
            }
        } catch (error: any) {
            createNotification('Failed to delete participant', 'error');
        }
    };

    const handleUpdateBuyerParticipant = async (participant: {
        id: number;
        name: string;
        role: string;
    }) => {
        try {
            const response = await axios.put(
                '/api/Marketplace/MarketplaceBuyerParticipants',
                participant
            );
            if (response.status === 200 || response.status === 204) {
                createNotification(
                    'Participant updated successfully',
                    'success'
                );
                await Promise.all([
                    fetchParticipants(),
                    fetchBuyerParticipants(),
                ]);
            } else {
                createNotification('Failed to update participant', 'error');
            }
        } catch (error: any) {
            createNotification('Failed to update participant', 'error');
        }
    };

    const handleSyncBuyerParticipants = async (selectedIds: number[]) => {
        try {
            const response = await axios.put(
                `/api/Marketplace/Engagements/${props.engagementId}/MeetingScheduling/${props.meetingId}/BuyerParticipants/Sync`,
                selectedIds
            );
            if (response.status === 200 || response.status === 204) {
                createNotification(
                    'Participants updated successfully',
                    'success'
                );
                await Promise.all([
                    fetchParticipants(),
                    fetchBuyerParticipants(),
                ]);
            } else {
                createNotification('Failed to update participants', 'error');
            }
        } catch (error: any) {
            createNotification('Failed to update participants', 'error');
        }
    };

    const canEditAttendanceMode = (participant: EngagementMeetingParticipant): boolean => {
        if (isParticipantsReadOnly || props.isReadOnly) return false;
        if (participant.source === MeetingParticipantSource.TeamMember && participant.userId === currentUser?.id) return true;
        if (canManageGlobalParticipants && party === TimelineParty.Buyer && participant.party === TimelineParty.Buyer) return true;
        if (party === TimelineParty.Seller && participant.party === TimelineParty.Seller) return true;
        return false;
    };

    const handleAttendanceModeChange = async (participant: EngagementMeetingParticipant, mode: MeetingAttendanceMode) => {
        const prev = participant.attendanceMode;
        setLocalParticipants((list) =>
            list.map((p) => p.id === participant.id ? { ...p, attendanceMode: mode } : p)
        );
        try {
            await axios.put(
                `/api/Marketplace/Engagements/${props.engagementId}/MeetingScheduling/${props.meetingId}/Participants/${participant.id}/AttendanceMode`,
                { attendanceMode: mode }
            );
        } catch {
            setLocalParticipants((list) =>
                list.map((p) => p.id === participant.id ? { ...p, attendanceMode: prev } : p)
            );
            createNotification('Failed to update attendance mode', 'error');
        }
    };

    const handleDeleteClick = (participant: EngagementMeetingParticipant) => {
        setParticipantToDelete(participant);
        setIsDeleteConfirmationVisible(true);
    };

    const handleDeleteConfirm = async () => {
        if (!participantToDelete) return;

        try {
            setIsDeleting(true);
            const response = await axios.delete(
                `/api/Marketplace/Engagements/${props.engagementId}/MeetingScheduling/${props.meetingId}/Participants/${participantToDelete.id}`
            );

            if (response.status === 200 || response.status === 204) {
                createNotification(
                    'Participant deleted successfully',
                    'success'
                );
                setIsDeleteConfirmationVisible(false);
                setParticipantToDelete(null);
                await Promise.all([fetchParticipants(), fetchEligibleRealUsers()]);
            } else {
                createNotification('Failed to delete participant', 'error');
            }
        } catch (error: any) {
            const status = error?.response?.status;
            if (status === 422) {
                createNotification(
                    'Cannot remove the last participant from a meeting',
                    'error'
                );
            } else {
                createNotification('Failed to delete participant', 'error');
            }
        } finally {
            setIsDeleting(false);
        }
    };

    const getPartyDisplayName = (partyType: TimelineParty): string => {
        switch (partyType) {
            case TimelineParty.Buyer:
                return 'Buyer';
            case TimelineParty.Seller:
                return 'Seller';
            default:
                return 'Unknown';
        }
    };

    const getColumns = () => {
        const columns: any[] = [
            {
                dataField: 'name',
                text: 'Name',
                headerClasses: 'name-column-header',
                sort: true,
            },
            {
                dataField: 'party',
                text: 'Representing',
                headerClasses: 'representing-column-header',
                sort: true,
                formatter: (cell: TimelineParty) => getPartyDisplayName(cell),
            },
            {
                dataField: 'role',
                text: 'Title/Role',
                headerClasses: 'role-column-header',
                sort: true,
            },
        ];

        if (props.meetingFormat === MeetingFormat.Mixed) {
            columns.push({
                dataField: 'attendanceMode',
                text: 'Joining as',
                headerClasses: 'attendance-mode-column-header',
                headerStyle: { minWidth: '140px' },
                style: { minWidth: '140px' },
                sort: false,
                formatter: (_: any, row: EngagementMeetingParticipant) => {
                    if (canEditAttendanceMode(row)) {
                        return (
                            <Input
                                type="select"
                                bsSize="sm"
                                value={row.attendanceMode ?? ''}
                                onChange={(e) =>
                                    handleAttendanceModeChange(
                                        row,
                                        Number(e.target.value) as MeetingAttendanceMode
                                    )
                                }>
                                <option value={MeetingAttendanceMode.Virtual}>
                                    {MeetingAttendanceModeLabels[MeetingAttendanceMode.Virtual]}
                                </option>
                                <option value={MeetingAttendanceMode.InPerson}>
                                    {MeetingAttendanceModeLabels[MeetingAttendanceMode.InPerson]}
                                </option>
                            </Input>
                        );
                    }
                    return (
                        <span>
                            {row.attendanceMode != null
                                ? MeetingAttendanceModeLabels[row.attendanceMode]
                                : '—'}
                        </span>
                    );
                },
            });
        }

        if (
            (party === TimelineParty.Buyer || party === TimelineParty.Seller) &&
            !props.isReadOnly &&
            !isParticipantsReadOnly
        ) {
            columns.push({
                dataField: 'actions',
                text: 'Actions',
                sort: false,
                formatter: (_: any, row: EngagementMeetingParticipant) => {
                    if (!canDelete(row)) {
                        return null;
                    }
                    return (
                        <div className="action-icons">
                            <FontAwesomeIcon
                                className="delete-button"
                                icon={faTrashCan}
                                onClick={() => handleDeleteClick(row)}
                                title="Delete participant"
                            />
                        </div>
                    );
                },
            });
        }

        return columns;
    };

    return (
        <div className="meeting-participants-modal">
            <Rodal
                width={800}
                height={600}
                visible={
                    props.isVisible &&
                    !isAddParticipantPopupVisible &&
                    !isDeleteConfirmationVisible
                }
                animation={'slideDown'}
                onClose={props.onClose}
                center>
                <ModalHeader>{'Meeting participants'}</ModalHeader>
                <ModalBody className="meeting-participants-modal-body">
                    {isLoading ? (
                        Helpers.renderTableLoader()
                    ) : (
                        <BootstrapTable
                            bootstrap4
                            striped
                            bordered
                            keyField="id"
                            data={sortedParticipants}
                            columns={getColumns()}
                            noDataIndication="No participants found"
                            rowStyle={{ height: '44px' }}
                        />
                    )}
                    {!props.isReadOnly && !isParticipantsReadOnly && (
                        <div className="add-participant-section">
                            {isSelfAbsent && (
                                <Button
                                    color="primary"
                                    disabled={isSubmitting}
                                    onClick={handleJoinMeeting}>
                                    Join Participants List
                                </Button>
                            )}
                            {canLeaveMeeting && (
                                <Button
                                    outline
                                    disabled={isDeleting}
                                    onClick={() =>
                                        handleDeleteClick(selfParticipant!)
                                    }>
                                    Leave Participants List
                                </Button>
                            )}
                            {canManageGlobalParticipants && (
                                <Button
                                    color="primary"
                                    onClick={() =>
                                        setIsAddParticipantPopupVisible(true)
                                    }>
                                    Add New Participant
                                </Button>
                            )}
                        </div>
                    )}
                </ModalBody>
            </Rodal>

            <AddParticipantFormPopup
                isVisible={isAddParticipantPopupVisible}
                onClose={() => setIsAddParticipantPopupVisible(false)}
                onSubmit={handleAddParticipant}
                isSubmitting={isSubmitting}
                buyerParticipants={
                    party === TimelineParty.Buyer
                        ? buyerParticipants
                        : undefined
                }
                initialSelectedBuyerIds={initialSelectedBuyerIds}
                onSyncBuyerParticipants={handleSyncBuyerParticipants}
                onDeleteBuyerParticipant={handleDeleteBuyerParticipant}
                onUpdateBuyerParticipant={handleUpdateBuyerParticipant}
                canManageGlobalParticipants={canManageGlobalParticipants}
                eligibleRealUsers={(isManagerBuyer || isManagerSeller) ? eligibleRealUsers : undefined}
                onAddRealUser={(isManagerBuyer || isManagerSeller) ? handleAddRealUser : undefined}
            />

            <ConfirmationModal
                isVisible={isDeleteConfirmationVisible}
                setIsVisible={setIsDeleteConfirmationVisible}
                header="Confirmation"
                confirmationText={
                    participantToDelete?.userId === currentUser?.id
                        ? 'Are you sure you want to leave the participants list?'
                        : `Are you sure you want to remove '${participantToDelete?.name}'?`
                }
                onConfirm={handleDeleteConfirm}
                nextButtonText="Confirm"
                cancelButtonText="Back"
                isLoading={isDeleting}
            />
        </div>
    );
};

export default MeetingParticipantsModal;
